import React from 'react';
import { normalizePublicLandingUrl } from '../publicLandingUrl';

type CoaLinkBarProps = {
  coaFiles: string[];
  showRemoveCoaButton?: (index: number) => void;
};

export default function CoaLinkBar({ coaFiles, showRemoveCoaButton }: CoaLinkBarProps) {
  const files = React.useMemo(() => {
    if (!Array.isArray(coaFiles)) return [];
    const normalizedFiles = coaFiles
      .map((file, sourceIndex) => ({ url: normalizePublicLandingUrl(file), sourceIndex }));
    return showRemoveCoaButton
      ? normalizedFiles
      : normalizedFiles.filter((file): file is { url: string; sourceIndex: number } => file.url !== null);
  }, [coaFiles, showRemoveCoaButton]);

  const [activeFileIndex, setActiveFileIndex] = React.useState(0);

  React.useEffect(() => {
    if (activeFileIndex >= files.length) {
      setActiveFileIndex(0);
    }
  }, [files.length, activeFileIndex]);

  const activeFile = files[activeFileIndex];
  const showViewButton = activeFile?.url !== null;

  if (files.length === 0) {
    return null;
  }

  return (
    <div data-testid="landing-coa-links">
      {files.length > 1 && (
        <div className="coa-selection-tabbar" style={{ margin: '8px 0 10px 0' }} data-testid="landing-coa-tabs">
          {files.map((file, index) => (
            <div
              key={`${file.url ?? 'invalid'}-${file.sourceIndex}`}
              className={`coa-selection-tab ${index === activeFileIndex ? 'selected' : ''}`}
              onClick={() => setActiveFileIndex(index)}
              data-testid={`landing-coa-tab-${index + 1}`}
              data-selected={index === activeFileIndex}
            >
              <div className="coa-selection-tab-content">
                <div className="coa-selection-tab-label">COA {index + 1}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showViewButton || showRemoveCoaButton) && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showViewButton && (
            <button
              type="button"
              onClick={() => {
                const destination = normalizePublicLandingUrl(activeFile?.url);
                if (destination) window.open(destination, '_blank', 'noopener,noreferrer');
              }}
              data-testid="landing-coa-view-action"
              data-active-coa={activeFileIndex + 1}
              style={{
                flex: 1,
                height: 40,
                padding: '10px 14px',
                backgroundColor: 'var(--brand)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                marginBottom: 16
              }}
            >
              View COA Results
            </button>
          )}
          {showRemoveCoaButton && (
            <button
              type="button"
              onClick={() => showRemoveCoaButton(activeFile.sourceIndex)}
              data-testid="landing-coa-remove-action"
              style={{
                height: 40,
                padding: '10px 14px',
                backgroundColor: 'transparent',
                color: 'var(--danger, #d32f2f)',
                border: '1px solid var(--danger, #d32f2f)',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                marginBottom: 16,
                whiteSpace: 'nowrap',
              }}
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}
